export interface OpponentEntry {
  id: string;
  displayName: string;
  element: number;
  lastSeenAt: string;
  castleLevel: number;
  armyPower: number;
  isShielded: boolean;
}

export interface ScoutReport {
  approxUnitsByClass: Record<string, number>;
  approxTotalPower: number;
  heroCount: number;
  isShielded: boolean;
  castleLevel: number;
  seed: number;
}

export interface MarchComposition {
  units: Record<string, number>;
  heroIds: string[];
}

export interface MarchEntry {
  id: string;
  attackerId: string;
  defenderId: string;
  campId: string | null;
  campName: string | null;
  direction: "outgoing" | "incoming";
  dispatchedAt: string;
  arrivesAt: string;
  composition: MarchComposition;
}

export interface AttackResponse {
  marchId: string;
  arrivesAt: string;
  travelSeconds: number;
  speedBonus: number;
}

// outcome: 0 = TeamA (attacker won), 1 = TeamB (defender/camp won), 2 = Draw.
export const BattleOutcome = {
  TeamA: 0,
  TeamB: 1,
  Draw: 2,
} as const;

import { ResourcePool } from "./castle";

export interface StackCasualtiesEntry {
  stackIndex: number;
  initialCount: number;
  killed: number;
  survivors: number;
}

export interface CasualtyBreakdownEntry {
  stackIndex: number;
  initialCount: number;
  survivors: number;
  lightWounded: number;
  heavyWounded: number;
  dead: number;
}

export const BattleEventKind = {
  Hit: 0,
  Crit: 1,
  MageAoeHit: 2,
  SkillDamage: 3,
  SkillHeal: 4,
  SkillBuff: 5,
} as const;

export interface BattleEventEntry {
  kind: number;
  team: number;
  sourceStackIndex: number | null;
  targetStackIndex: number | null;
  heroIndex: number | null;
  amount: number;
}

export interface BattleRoundEntry {
  number: number;
  events: BattleEventEntry[];
  aliveAfterA: number[];
  aliveAfterB: number[];
}

export interface BattleReportEntry {
  seed: number;
  outcome: number;
  rounds: number;
  remainingPowerA: number;
  remainingPowerB: number;
  armyACasualties: StackCasualtiesEntry[];
  armyBCasualties: StackCasualtiesEntry[];
  roundLog: BattleRoundEntry[] | null;
}

export interface BattleResolutionEntry {
  report: BattleReportEntry;
  lootForAttacker: ResourcePool;
  shieldDurationForDefender: string;
  attackerBreakdown: CasualtyBreakdownEntry[];
  defenderBreakdown: CasualtyBreakdownEntry[];
  heroXpForAttacker: number;
  attackerUnitIds: string[] | null;
  defenderUnitIds: string[] | null;
}

export interface BattleLogEntry {
  id: string;
  attackerId: string;
  defenderId: string | null;
  campId: string | null;
  outcome: number;
  resolution: BattleResolutionEntry;
  resolvedAt: string;
  // Server-classified battle type: "pvp" | "pve" | "rally". Older payloads
  // may not include it — treat missing as "pvp".
  kind?: "pvp" | "pve" | "rally";
}
